import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { cameraLog } from '@renderer/utils/cameras/cameraLog'
import { describeError } from '@renderer/utils/telemetry/boothLogClient'

interface CameraPromptProps {
  onStart: () => void | Promise<void>
}

export const CameraPrompt: React.FC<CameraPromptProps> = ({ onStart }) => {
  const { t } = useTranslation()
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionAttempts, setConnectionAttempts] = useState(0)

  const mountedRef = useRef(true)

  // Reset connection state when component mounts
  useEffect(() => {
    mountedRef.current = true
    setIsConnecting(false)
    setConnectionAttempts(0)
    // Every road to this screen means the booth lost its camera; the lines before
    // this one in the log say which.
    cameraLog.uiErrorShown('CameraPrompt', 'tap to start camera')
    return (): void => {
      mountedRef.current = false
    }
  }, [])

  const handleClick = async (): Promise<void> => {
    if (isConnecting) {
      return // Prevent multiple simultaneous connection attempts
    }

    setIsConnecting(true)
    setConnectionAttempts((prev) => prev + 1)

    try {
      await onStart()
      // A successful connect unmounts this prompt, so resetting here is harmless there.
      // But onStart also RESOLVES when a recovery reconnect fails and the caller keeps
      // the prompt open for a retry — and then "Connecting…" stayed on screen forever,
      // every tap short-circuited above, and the operator's only way out was a refresh.
      if (mountedRef.current) setIsConnecting(false)
    } catch (error) {
      console.error('CameraPrompt: Connection failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(t('camera.prompt.connectionFailed', `Connection failed: ${errorMessage}`), {
        hideAfter: 5
      })
      cameraLog.uiErrorShown('CameraPrompt', 'connection failed toast', {
        error: describeError(error)
      })
      // Reset connecting state to allow retry
      setIsConnecting(false)
      // Don't close the prompt - let user retry
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 cursor-pointer"
      onClick={handleClick}
    >
      <div className="text-center text-white">
        <h2 className="text-3xl font-bold mb-4">
          {isConnecting
            ? t('camera.prompt.connecting', 'Connecting...')
            : t('camera.prompt.title', 'Click to Start Camera')}
        </h2>
        <p className="text-lg opacity-80">
          {isConnecting
            ? t('camera.prompt.pleaseWait', 'Please wait...')
            : t('camera.prompt.instruction', 'Click anywhere to connect your camera')}
        </p>
        {connectionAttempts > 1 && !isConnecting && (
          <p className="text-sm opacity-60 mt-2">
            {t('camera.prompt.retryHint', 'Click again to retry')}
          </p>
        )}
      </div>
    </div>
  )
}
